// Shared date / ISO-week helpers — THE single implementation.
// Previously ~6 components carried their own copies (in two subtly different
// variants); every date rule lives here now. All functions work in LOCAL time
// (Europe/Berlin in practice) unless stated otherwise.
import { useEffect, useState } from "react";

export function pad2(n) { return String(n).padStart(2, "0"); }

/** Monday 00:00 (local) of the week containing `d`. */
export function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  date.setDate(date.getDate() + (day === 0 ? -6 : 1 - day));
  date.setHours(0, 0, 0, 0);
  return date;
}

export function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

/** Local "YYYY-MM-DD" — never toISOString(), which would shift the day in UTC. */
export function formatISODate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** "YYYY-MM-DD" → local-midnight Date (new Date("YYYY-MM-DD") would parse as UTC). */
export function parseISODateLocal(s) {
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** ISO date string shifted by n days. */
export function isoAddDays(iso, n) {
  const d = parseISODateLocal(iso);
  d.setDate(d.getDate() + n);
  return formatISODate(d);
}

/** "DD.MM.YY" for list rows. Accepts an ISO string (or anything slicing to one). */
export function fmtDateDE(iso) {
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return `${pad2(d)}.${pad2(m)}.${String(y).slice(-2)}`;
}

/** JS getDay() → ISO weekday 1=Mon..7=Sun. */
export function isoWeekday(date) { const w = date.getDay(); return w === 0 ? 7 : w; }

/** ISO 8601 week number (the week containing the date's Thursday). */
export function getISOWeek(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
}

/** The year the ISO week belongs to (Jan 1st can sit in week 52/53 of the PREVIOUS year). */
export function getISOWeekYear(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  return date.getUTCFullYear();
}

/** ISO week key "YYYY-WW" — the format weekly_hours_overrides is keyed by. */
export function weekKeyOf(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() + 4 - day);
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getFullYear()}-${pad2(weekNo)}`;
}

/** Monday (local Date) of ISO week `week` in ISO year `year`. */
export function getMondayOfWeek(year, week) {
  const jan4 = new Date(year, 0, 4);
  const day = jan4.getDay();
  const mondayWeek1 = new Date(year, 0, 4 + (day === 0 ? -6 : 1 - day));
  return addDays(mondayWeek1, (week - 1) * 7);
}

/** Number of ISO weeks in `year` (52 or 53). */
export function lastWeekOfYear(year) {
  return getISOWeek(new Date(year, 11, 28));
}

/**
 * Which month a week belongs to: the month containing MOST of its days, which
 * for a Mon–Sun week is exactly the Thursday's month. Both the self hours
 * account and the admin year view group by this same rule.
 * Returns { month: 0-11, year }.
 */
export function monthOfWeek(monday) {
  const thu = addDays(getMonday(monday), 3);
  return { month: thu.getMonth(), year: thu.getFullYear() };
}

/** "YYYY-MM-DD" + "HH:MM" (local) → UTC ISO string for the API. */
export function combineDateTime(dateStr, timeStr) {
  const d = new Date(`${dateStr}T${timeStr}`);
  // Beim Tippen steht hier zwischendurch "1" oder "12:6" — dann ist das Datum
  // ungueltig und toISOString() warf "Invalid time value", was die ganze
  // Ansicht abstuerzen liess. Aufrufer pruefen jetzt auf null.
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/**
 * Today's local ISO date as reactive state. Updates shortly after midnight and
 * whenever the tab regains focus/visibility (background tabs throttle timers),
 * so a tab left open over midnight rolls to the new day without a reload —
 * and without discarding unsaved form state the way location.reload() would.
 */
export function useToday() {
  const [today, setToday] = useState(() => formatISODate(new Date()));
  useEffect(() => {
    let timer;
    const refresh = () => {
      const iso = formatISODate(new Date());
      setToday((prev) => (prev === iso ? prev : iso));
    };
    const schedule = () => {
      const now = new Date();
      // 5 s past midnight so a slightly-early timer can't fire on the old day.
      const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 5);
      timer = setTimeout(() => { refresh(); schedule(); }, next.getTime() - now.getTime());
    };
    schedule();
    const onWake = () => { if (!document.hidden) refresh(); };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, []);
  return today;
}
